import { useState, useEffect } from 'react';

/**
 * Custom hook to handle cart management in header
 * Consolidates 4 useEffect hooks and cart-related logic
 */
export const useHeaderCart = ({
  cartItemsList,
  currentLanguage,
  cartItems,
  restaurant,
  loggedInSuccess,
  removeItemFromCart,
  updateOrderItem,
  updateOrderItemSuccess,
  getCartItems
}) => {
  const [innerCartItems, setInnerCartItems] = useState([]);
  const [itemQuantity, setItemQuantity] = useState(0);
  const [openModalCart, setOpenModalCart] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [cartItemDeleted, setCartItemDeleted] = useState(false);
  const [cartItemDeletedIndex, setCartItemDeletedIndex] = useState(null);

  // Fetch cart items when language or cart changes
  useEffect(() => {
    let cart = localStorage.getItem('cart');
    if (cart) cart = JSON.parse(cart);
    
    if (cart?.length > 0 && currentLanguage) {
      fetchCartItems();
    } else if (cart?.length === 0) {
      localStorage.removeItem('orderId');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('cart');
    }
  }, [currentLanguage, cartItems]);

  // Update inner cart items and calculate total quantity
  useEffect(() => {
    if (cartItemsList?.data) {
      setInnerCartItems(cartItemsList.data);
      
      const totalQuantity = cartItemsList.data.reduce((acc, item) => {
        return acc + (item.quantity || 0);
      }, 0);
      setItemQuantity(totalQuantity);
    }
  }, [cartItemsList]);

  // Check for expired cart items (older than 1 day)
  useEffect(() => {
    const sessionId = localStorage.getItem("sessionId");
    const orderId = localStorage.getItem("orderId");
    
    if (restaurant && !loggedInSuccess && sessionId && orderId) {
      innerCartItems.forEach((item, index) => {
        const today = new Date();
        const itemAddedTime = new Date(item.created_at);
        const timeDifference = today - itemAddedTime;
        const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        
        if (daysDifference >= 1) {
          setCartItemDeleted(true);
          setCartItemDeletedIndex(index);
          
          const orderData = {
            sessionId,
            orderId,
            cartId: item._id,
            user_id: '65fa8d79b94da3c8da28b50f',
            restaurant_branch_id: restaurant?.branches[0]?._id,
            cartDetails: {
              quantity: item.quantity,
              price: item.product.price * item.quantity,
              category_id: item.product.category_id,
              product_id: item.product._id,
              status: 0
            }
          };
          updateOrderItem(orderData);
        }
      });
    }
  }, [restaurant]);

  // Remove item from cart when update is successful
  useEffect(() => {
    if (updateOrderItemSuccess && cartItemDeleted && cartItemDeletedIndex !== null) {
      removeItemFromCart(cartItemDeletedIndex);
      setCartItemDeleted(false);
      setCartItemDeletedIndex(null);
    }
  }, [updateOrderItemSuccess, cartItemDeleted, cartItemDeletedIndex]);

  const fetchCartItems = () => {
    const orderId = localStorage.getItem('orderId');
    if (orderId && currentLanguage?._id) {
      getCartItems({ order_id: orderId, language_id: currentLanguage._id });
    }
  };

  const handleOpenCart = () => {
    const isModalOpen = localStorage.getItem('cartModal');
    if (isModalOpen !== 'true') {
      localStorage.setItem('cartModal', false);
      setOpenModalCart(true);
    }
  };

  const handleCloseCart = () => {
    localStorage.setItem('cartModal', false);
    setOpenModalCart(false);
  };

  return {
    innerCartItems,
    itemQuantity,
    openModalCart,
    totalPrice,
    totalQuantity,
    setOpenModalCart,
    setTotalPrice,
    setTotalQuantity,
    handleOpenCart,
    handleCloseCart
  };
};

